import { InvalidArgumentError, NotSupportedError } from './clientErrors';
function trimTrailingSlash(url) {
    return url.slice(-1) === '/' ? url.slice(0, -1) : url;
}
function trimLeadingSlash(url) {
    return url[0] === '/' ? url.slice(1) : url;
}
export class HttpError extends Error {
    constructor(message, statusCode, traceId) {
        super(traceId ? `${message}. TraceId: ${traceId}` : message);
        Object.defineProperty(this, "statusCode", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "payload", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "traceId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.name = 'HttpError';
        this.payload = message;
        this.traceId = traceId;
        this.statusCode = statusCode;
        Object.setPrototypeOf(this, HttpError.prototype);
    }
}
export class HttpClientResponse {
    constructor(fetchResponse) {
        Object.defineProperty(this, "fetchResponse", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.fetchResponse = fetchResponse;
    }
    get status() {
        return this.fetchResponse.status;
    }
    async json() {
        const responseData = await this.fetchResponse.json();
        return responseData;
    }
}
export class HttpClientRequestWrapper {
    constructor(execute) {
        Object.defineProperty(this, "executeRequest", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "acceptCodes", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "queryParams", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {}
        });
        Object.defineProperty(this, "signal", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "bodyFactory", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "contentType", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "then", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (...args) => {
                const resultPromise = (async () => {
                    const result = await this.executeRequest(this.createConfig());
                    if (result instanceof HttpClientResponse) {
                        return result;
                    }
                    else {
                        return new HttpClientResponse(result);
                    }
                })();
                return resultPromise.then(...args);
            }
        });
        this.executeRequest = execute;
    }
    withAbortSignal(signal) {
        this.signal = signal;
        return this;
    }
    withContentType(contentType) {
        this.contentType = contentType;
        return this;
    }
    withBodyFromBuffer(body) {
        if (body === false || body == undefined) {
            return this;
        }
        this.bodyFactory = () => body;
        return this;
    }
    withJsonBody(serializableBody) {
        if (serializableBody !== false && serializableBody != undefined) {
            this.bodyFactory = () => JSON.stringify(serializableBody);
        }
        return this;
    }
    withQueryParams(queryParams) {
        if (queryParams) {
            this.queryParams = {
                ...this.queryParams,
                ...queryParams,
            };
        }
        return this;
    }
    withRawBody(body) {
        if (body !== false && body != undefined) {
            this.bodyFactory = () => body;
        }
        return this;
    }
    async json() {
        const result = await this;
        return result.json();
    }
    acceptCode(code) {
        this.acceptCodes.push(code);
        return this;
    }
    createConfig() {
        return {
            signal: this.signal,
            bodyFactory: this.bodyFactory,
            acceptCodes: this.acceptCodes,
            queryParams: this.queryParams,
            contentType: this.contentType,
        };
    }
    static create(fn) {
        return new HttpClientRequestWrapper(fn);
    }
}
export class HttpClient {
    constructor(fetchImpl, baseUrl, authenticationProvider, defaultQueryParams) {
        Object.defineProperty(this, "fetchImpl", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "baseUrl", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "authenticationProvider", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "defaultParams", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {}
        });
        this.fetchImpl = fetchImpl;
        this.baseUrl = baseUrl == undefined ? baseUrl : trimTrailingSlash(baseUrl);
        this.defaultParams = defaultQueryParams;
        this.authenticationProvider = authenticationProvider;
    }
    get(url) {
        return this.method('GET')(url);
    }
    post(url) {
        return this.method('POST')(url);
    }
    put(url) {
        return this.method('PUT')(url);
    }
    patch(url) {
        return this.method('PATCH')(url);
    }
    delete(url) {
        return this.method('DELETE')(url);
    }
    method(method) {
        return (url) => HttpClientRequestWrapper.create(async (config) => {
            const fetchUrl = this.url(url, config.queryParams);
            let request = this.appendDefaultRequestHeaders({ method: method, credentials: 'include' }, config.contentType);
            request = this.authenticationProvider(request);
            if (config.signal) {
                request.signal = config.signal;
            }
            if (config.bodyFactory) {
                if (method === 'GET') {
                    throw new InvalidArgumentError('method', 'Body is not allowed for GET requests');
                }
                request.body = config.bodyFactory();
            }
            const response = await this.fetchImpl(fetchUrl, request);
            await this.checkStatus(response, config.acceptCodes);
            return response;
        });
    }
    appendDefaultRequestHeaders(options, contentType) {
        options.headers = options.headers ?? {};
        options.headers = {
            ...options.headers,
            'Content-Type': contentType ?? 'application/json',
            'Cache-Control': 'no-cache, no-store',
            Pragma: 'no-cache',
            Expires: '0',
        };
        return options;
    }
    url(path, params) {
        let result = path;
        if (this.baseUrl != undefined) {
            result = this.baseUrl + '/' + trimLeadingSlash(result);
        }
        const resultParams = { ...this.defaultParams, ...params };
        if (path.includes('?') && params != undefined && Object.keys(resultParams).length > 0) {
            throw new NotSupportedError(`Passing search params in url and via params object is not supported`);
        }
        result = result + this.createQueryString(resultParams);
        return result;
    }
    async checkStatus(response, acceptCodes) {
        if (!(response.status >= 200 && response.status < 300) || (acceptCodes ?? []).includes(response.status)) {
            const errorText = await response.text();
            let serverResponse;
            try {
                serverResponse = JSON.parse(errorText);
            }
            catch (e) {
                serverResponse = undefined;
            }
            throw new HttpError(serverResponse?.error ? JSON.stringify(serverResponse?.error) : errorText, response.status, serverResponse?.traceId);
        }
    }
    postSync(url, params, data) {
        const request = new XMLHttpRequest();
        request.open('POST', this.url(url, params), false);
        request.withCredentials = true;
        request.setRequestHeader('Content-Type', 'application/json; charset=UTF-8');
        request.send(JSON.stringify(data));
        return request.status;
    }
    makeParamValueString(param, value) {
        return `${encodeURIComponent(param)}=${encodeURIComponent(value)}`;
    }
    createQueryString(params) {
        if (params == undefined) {
            return '';
        }
        const params2 = params;
        let result = Object.keys(params)
            .map((key) => {
            const value = params2[key];
            if (typeof value === 'string') {
                return this.makeParamValueString(key, value);
            }
            if (typeof value === 'number' || typeof value === 'boolean') {
                return this.makeParamValueString(key, value.toString());
            }
            if (Array.isArray(value)) {
                return value
                    .map((item) => {
                    if (typeof item === 'string') {
                        return this.makeParamValueString(key, item);
                    }
                    if (typeof item === 'number') {
                        return this.makeParamValueString(key, item.toString());
                    }
                    return undefined;
                })
                    .filter((x) => x != undefined)
                    .join('&');
            }
            return undefined;
        })
            .filter((x) => x != undefined)
            .join('&');
        result = result ? '?' + result : '';
        return result;
    }
    createFetchImpl() {
        const fetchWithOptions = (input, init) => {
            if (typeof input === 'string') {
                const fetchUrl = this.url(input, undefined);
                init = init ?? {};
                init.headers = init.headers ?? {};
                init.headers = {
                    ...init.headers,
                    'Cache-Control': 'no-cache, no-store',
                    Pragma: 'no-cache',
                    Expires: '0',
                };
                init = this.authenticationProvider(init);
                return this.fetchImpl(fetchUrl, init);
            }
            else {
                const fetchUrl = this.url(input.url, undefined);
                let requestInit = {
                    headers: {
                        ['Cache-Control']: 'no-cache, no-store',
                        ['Pragma']: 'no-cache',
                        ['Expires']: '0',
                        ...input.headers,
                    },
                    signal: input.signal,
                    cache: input.cache,
                    credentials: input.credentials,
                    integrity: input.integrity,
                    keepalive: input.keepalive,
                    method: input.method,
                    mode: input.mode,
                    redirect: input.redirect,
                    referrer: input.referrer,
                    referrerPolicy: input.referrerPolicy,
                };
                requestInit = this.authenticationProvider(requestInit);
                return this.fetchImpl(fetchUrl, requestInit);
            }
        };
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return fetchWithOptions;
    }
}
